/**
 * Module-level native token store.
 *
 * Bridges the React-based useNativeAuthBridge hook and the module-level
 * getToken() function in MsalAuthConfig.ts. This store is imported by both
 * and provides shared state without requiring React context.
 */

export interface NativeTokenPayload {
  idToken: string;
  accessToken: string;
  account: {
    homeAccountId: string;
    localAccountId: string;
    environment: string;
    tenantId: string;
    username: string;
    name?: string;
  };
  scopes: string[];
  expiresOn: number; // epoch milliseconds
  tokenType: string;
}

type NativeBridgeRefreshCallback = () => Promise<NativeTokenPayload>;

// --- Module-level state ---

let _nativeTokens: NativeTokenPayload | null = null;
let _isNativeContext = false;
let _nativeBridgeRefresh: NativeBridgeRefreshCallback | null = null;

// Promise-based wait mechanism for early getToken() calls
let _tokenReadyResolve: ((accessToken: string) => void) | null = null;
let _tokenReadyPromise: Promise<string> | null = null;

// Request refresh 60 seconds before actual expiry
const TOKEN_EXPIRY_BUFFER_MS = 60_000;

export function setNativeTokens(tokens: NativeTokenPayload): void {
  _nativeTokens = tokens;
  console.info('[NativeTokenStore] Tokens stored', {
    expiresOn: new Date(tokens.expiresOn).toISOString(),
    hasAccessToken: !!tokens.accessToken,
    hasIdToken: !!tokens.idToken,
  });

  // Resolve any pending waitForNativeTokens() callers
  if (_tokenReadyResolve) {
    _tokenReadyResolve(tokens.accessToken);
    _tokenReadyResolve = null;
    _tokenReadyPromise = null;
  }
}

export function getNativeTokens(): NativeTokenPayload | null {
  return _nativeTokens;
}

export function isNativeTokenValid(): boolean {
  if (!_nativeTokens) return false;
  return Date.now() < _nativeTokens.expiresOn - TOKEN_EXPIRY_BUFFER_MS;
}

export function setIsNativeContext(isNative: boolean): void {
  _isNativeContext = isNative;
}

export function getIsNativeContext(): boolean {
  return _isNativeContext;
}

export function registerNativeBridgeRefresh(cb: NativeBridgeRefreshCallback): void {
  _nativeBridgeRefresh = cb;
}

export function unregisterNativeBridgeRefresh(): void {
  _nativeBridgeRefresh = null;
}

/**
 * Request a token refresh via the native bridge.
 * Called by getToken() when native tokens are expired.
 * The registered callback (from useNativeAuthBridge) handles the 2-step strategy.
 */
export async function refreshViaNativeBridge(): Promise<string> {
  if (!_nativeBridgeRefresh) {
    throw new Error('[NativeTokenStore] No native bridge refresh callback registered');
  }

  const freshTokens = await _nativeBridgeRefresh();
  setNativeTokens(freshTokens);
  return freshTokens.accessToken;
}

export function clearNativeTokens(): void {
  _nativeTokens = null;
}

/**
 * Wait for native tokens to become available.
 * Used by getToken() when we detect a native webview but the handshake
 * hasn't completed yet (tokens not arrived).
 */
export function waitForNativeTokens(timeoutMs = 45_000): Promise<string> {
  if (isNativeTokenValid()) {
    return Promise.resolve(_nativeTokens!.accessToken);
  }
  if (!_tokenReadyPromise) {
    _tokenReadyPromise = new Promise<string>((resolve, reject) => {
      _tokenReadyResolve = resolve;
      setTimeout(() => {
        if (_tokenReadyResolve) {
          _tokenReadyResolve = null;
          _tokenReadyPromise = null;
          reject(new Error('[NativeTokenStore] Timed out waiting for native tokens'));
        }
      }, timeoutMs);
    });
  }
  return _tokenReadyPromise;
}

/**
 * Synchronous check for native webview environment.
 * Works before handshake completes — used to suppress B2C redirects early.
 */
export function isNativeWebview(): boolean {
  return !!(
    (window as unknown as { webkit?: { messageHandlers?: { appInterface?: unknown } } }).webkit?.messageHandlers
      ?.appInterface || (window as unknown as { androidApp?: unknown }).androidApp
  );
}
